import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CalendarClock, MapPin } from 'lucide-react';
import {
  GATHERING_LIMITS,
  canRing,
  gatheringEnded,
  replyCounts,
  type GatheringReply,
  type GatheringState,
} from '@twinden/shared';
import { formatGatheringTime, myGatheringReply } from '../../lib/gatherings';
import type { ChatMessage } from './messageUtils';
import { useT } from '../../i18n';

// A gathering in the chat (M71, D282–D288): the words were opened on the
// device; who's coming, who's late and who's at the table are metadata
// everyone in the chat sees, by name.

const CARD_CLOCK_MS = 15_000;

export interface GatheringActions {
  reply: (
    message: ChatMessage,
    reply: GatheringReply,
    lateMinutes?: number
  ) => void;
  takeBack: (message: ChatMessage) => void;
  arrive: (message: ChatMessage) => void;
  ring: (message: ChatMessage) => void;
  cancel: (message: ChatMessage) => void;
  /** What you call this person (D143), or their name. */
  nameOf: (userId: string) => string;
}

export function GatheringCard({
  message,
  currentUserId,
  participantIds,
  isAdmin,
  actions,
  now: fixedNow,
}: {
  message: ChatMessage;
  currentUserId: string | undefined;
  /** Everyone in the chat: the counts and "haven't said" come from it. */
  participantIds: string[];
  /** A family root or admin may ring too (D286). */
  isAdmin: boolean;
  actions: GatheringActions;
  /** A fixed clock (tests); otherwise the card keeps its own. */
  now?: number;
}) {
  const t = useT();
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const content = message.e2ee?.envelope?.gathering;
  const gathering = message.gathering;
  // Its own clock while it's open: Come now comes back 3 minutes after a
  // ring and the card turns Ended on time, with nothing else re-rendering it.
  const [tick, setTick] = useState(() => Date.now());
  const live = Boolean(gathering && !gathering.cancelledAt && !gathering.ended);
  useEffect(() => {
    if (!live || fixedNow !== undefined) return;
    const timer = window.setInterval(() => setTick(Date.now()), CARD_CLOCK_MS);
    return () => window.clearInterval(timer);
  }, [live, fixedNow]);
  const now = fixedNow ?? tick;
  if (!content || !gathering) {
    return (
      <Text fontStyle="italic" color="muted" fontSize="15px">
        {t('chat.message.updateApp')}
      </Text>
    );
  }

  const cancelled = Boolean(gathering.cancelledAt);
  const over = gathering.ended || gatheringEnded(gathering, now);
  const mine = myGatheringReply(gathering, currentUserId);
  const organiser = currentUserId === gathering.createdBy;
  const counts = replyCounts(gathering, participantIds);
  const countsLine = [
    counts.coming > 0 && t('chat.gathering.countComing', { n: counts.coming }),
    counts.late > 0 && t('chat.gathering.countLate', { n: counts.late }),
    counts.not > 0 && t('chat.gathering.countNot', { n: counts.not }),
    counts.unanswered > 0 &&
      t('chat.gathering.countUnanswered', { n: counts.unanswered }),
  ]
    .filter(Boolean)
    .join(' · ');

  const ring = canRing(gathering, now);
  const ringReason =
    !ring.ok && ring.reason === 'LIMIT'
      ? t('chat.gathering.ringsLimit')
      : !ring.ok && ring.reason === 'TOO_SOON'
        ? t('chat.gathering.ringsTooSoon')
        : null;
  // Rings off or over: no button at all, there is nothing to wait for.
  const showRing = (organiser || isAdmin) && !over && gathering.rings;

  const choose = (reply: GatheringReply) => {
    if (mine?.reply === reply) actions.takeBack(message);
    else actions.reply(message, reply, undefined);
  };

  return (
    // Never wider than its bubble on a narrow phone (M68, D259).
    <Box
      minW={{ base: 'min(240px, 100%)', md: 'min(280px, 100%)' }}
      maxW="320px"
      data-testid="gathering"
    >
      <HStack spacing={2} align="flex-start">
        <Box color="accent" pt="3px" flexShrink={0}>
          <CalendarClock size={18} aria-hidden="true" />
        </Box>
        <Text fontWeight={700} overflowWrap="anywhere">
          {content.title}
        </Text>
      </HStack>
      <Text fontSize="14px" fontWeight={600}>
        {formatGatheringTime(gathering.startsAt, now)}
      </Text>
      {content.place && (
        <HStack spacing={1} fontSize="14px" color="muted" align="flex-start">
          <Box pt="2px" flexShrink={0}>
            <MapPin size={14} aria-hidden="true" />
          </Box>
          <Text overflowWrap="anywhere">{content.place}</Text>
        </HStack>
      )}
      {content.note && (
        <Text fontSize="14px" pt={1} overflowWrap="anywhere">
          {content.note}
        </Text>
      )}

      {cancelled ? (
        <Text pt={2} fontWeight={700} color="danger">
          {t('chat.gathering.cancelled')}
        </Text>
      ) : over ? (
        <Text pt={2} fontWeight={700} color="muted">
          {t('chat.gathering.ended')}
        </Text>
      ) : null}

      <Text fontSize="13px" color="muted" pt={2}>
        {countsLine || t('chat.gathering.noneYet')}
      </Text>
      {counts.arrived > 0 && (
        <Text fontSize="13px" fontWeight={700}>
          {t('chat.gathering.atTable', {
            n: counts.arrived,
            total: participantIds.length,
          })}
        </Text>
      )}
      <People
        gathering={gathering}
        participantIds={participantIds}
        currentUserId={currentUserId}
        nameOf={actions.nameOf}
      />

      {!over && (
        <Stack spacing={2} pt={2}>
          <Flex gap={2} flexWrap="wrap">
            <ReplyButton
              label={t('chat.gathering.coming')}
              on={mine?.reply === 'coming'}
              onClick={() => choose('coming')}
            />
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                minH="44px"
                variant="outline"
                colorScheme={mine?.reply === 'late' ? 'brand' : 'gray'}
                borderColor={mine?.reply === 'late' ? 'accent' : 'line'}
                bg={mine?.reply === 'late' ? 'accent.tint' : 'surface'}
                aria-pressed={mine?.reply === 'late'}
              >
                {t('chat.gathering.late')}
              </MenuButton>
              <MenuList>
                {mine?.reply === 'late' && (
                  <MenuItem onClick={() => actions.takeBack(message)}>
                    {t('chat.gathering.takeBack')}
                  </MenuItem>
                )}
                {GATHERING_LIMITS.LATE_MINUTES.map((minutes) => (
                  <MenuItem
                    key={minutes}
                    onClick={() => actions.reply(message, 'late', minutes)}
                  >
                    {t('chat.gathering.lateBy', { n: minutes })}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <ReplyButton
              label={t('chat.gathering.not')}
              on={mine?.reply === 'not'}
              onClick={() => choose('not')}
            />
          </Flex>
          {mine && mine.reply !== 'not' && !mine.arrivedAt && (
            <Button
              colorScheme="brand"
              minH="44px"
              onClick={() => actions.arrive(message)}
            >
              {t('chat.gathering.here')}
            </Button>
          )}
          {(showRing || organiser) && (
            <Flex gap={2} align="center" flexWrap="wrap">
              {showRing && (
                <Button
                  size="sm"
                  minH="44px"
                  variant="outline"
                  colorScheme="brand"
                  isDisabled={!ring.ok}
                  onClick={() => actions.ring(message)}
                >
                  {t('chat.gathering.comeNow')}
                </Button>
              )}
              {organiser && !confirmingCancel && (
                <Button
                  size="sm"
                  minH="44px"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => setConfirmingCancel(true)}
                >
                  {t('chat.gathering.cancel')}
                </Button>
              )}
            </Flex>
          )}
          {organiser && confirmingCancel && (
            <Box
              role="alertdialog"
              aria-label={t('chat.gathering.cancelConfirm')}
            >
              <Text fontWeight={700} pb={2}>
                {t('chat.gathering.cancelConfirm')}
              </Text>
              <Flex gap={2}>
                <Button
                  size="sm"
                  minH="44px"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => setConfirmingCancel(false)}
                >
                  {t('chat.gathering.cancelKeep')}
                </Button>
                <Button
                  size="sm"
                  minH="44px"
                  colorScheme="red"
                  onClick={() => {
                    setConfirmingCancel(false);
                    actions.cancel(message);
                  }}
                >
                  {t('chat.gathering.cancelYes')}
                </Button>
              </Flex>
            </Box>
          )}
          {(organiser || isAdmin) && !over && !gathering.rings && (
            <Text fontSize="12px" color="muted">
              {t('chat.gathering.ringsOff')}
            </Text>
          )}
          {showRing && ringReason && (
            <Text fontSize="12px" color="muted">
              {ringReason}
            </Text>
          )}
        </Stack>
      )}
    </Box>
  );
}

function ReplyButton({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="sm"
      minH="44px"
      variant="outline"
      colorScheme={on ? 'brand' : 'gray'}
      borderColor={on ? 'accent' : 'line'}
      bg={on ? 'accent.tint' : 'surface'}
      aria-pressed={on}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

// People grouped by answer; a ✓ marks those at the table.
function People({
  gathering,
  participantIds,
  currentUserId,
  nameOf,
}: {
  gathering: GatheringState;
  participantIds: string[];
  currentUserId: string | undefined;
  nameOf: (userId: string) => string;
}) {
  const t = useT();
  const label = (id: string) =>
    id === currentUserId ? t('common.you') : nameOf(id);
  const rowOf = (id: string) => gathering.replies.find((r) => r.userId === id);
  const groups: { key: string; title: string; names: string[] }[] = [
    { key: 'coming', title: t('chat.gathering.groupComing'), names: [] },
    { key: 'late', title: t('chat.gathering.groupLate'), names: [] },
    { key: 'not', title: t('chat.gathering.groupNot'), names: [] },
  ];
  for (const id of participantIds) {
    const row = rowOf(id);
    // Who hasn't answered is a count only, never a list of names.
    if (!row) continue;
    const group = groups.find((g) => g.key === row.reply)!;
    const name =
      row?.reply === 'late' && row.lateMinutes !== null
        ? t('chat.gathering.lateName', {
            name: label(id),
            n: row.lateMinutes,
          })
        : label(id);
    group.names.push(row?.arrivedAt ? `✓ ${name}` : name);
  }
  return (
    <Stack spacing={1} pt={2}>
      {groups
        .filter((g) => g.names.length > 0)
        .map((g) => (
          <Text key={g.key} fontSize="13px" overflowWrap="anywhere">
            <Text as="span" fontWeight={700}>
              {g.title}:{' '}
            </Text>
            {g.names.join(', ')}
          </Text>
        ))}
    </Stack>
  );
}
